import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, PhoneCall, ShieldCheck, Layers, Settings, Compass,
  MessageCircle, X, Clock, User
} from "lucide-react";
import Logo from "@/components/Logo";
import "@/pages/thankyou.css";

const LIVE_FEED_SEED = [
  { initials: "SC", name: "Sofía C.", city: "Rosario", minutesAgo: 5 },
  { initials: "AV", name: "Andrés V.", city: "Córdoba", minutesAgo: 3 },
  { initials: "MJ", name: "María José R.", city: "Mendoza", minutesAgo: 1 },
  { initials: "CM", name: "Carlos M.", city: "CABA", minutesAgo: 8 },
  { initials: "LP", name: "Lucía P.", city: "La Plata", minutesAgo: 12 },
  { initials: "MT", name: "Matías T.", city: "San Miguel de Tucumán", minutesAgo: 15 },
  { initials: "RB", name: "Rocío B.", city: "Mar del Plata", minutesAgo: 18 },
  { initials: "FN", name: "Federico N.", city: "Bariloche", minutesAgo: 22 },
];

export default function ThankYou() {
  const [progress, setProgress] = useState(0); // 0 -> just received, 1 -> advisor assigned, 2 -> connection
  const [feed, setFeed] = useState(LIVE_FEED_SEED.slice(0, 4));
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [chatBadge, setChatBadge] = useState(1);

  const submission = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("zr_last_submission");
      if (raw) return JSON.parse(raw);
    } catch (_) { /* noop */ }
    return null;
  }, []);

  const submittedAt = useMemo(() => {
    const d = submission?.submittedAt ? new Date(submission.submittedAt) : new Date();
    return d.toLocaleString("es-AR", {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  }, [submission]);

  const reference = useMemo(() => {
    if (submission?.reference) return submission.reference;
    const now = new Date();
    const yymm = `${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, "0")}`;
    const rand = Math.floor(Math.random() * 90000 + 10000);
    return `ZR-${yymm}-${rand}`;
  }, [submission]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "Solicitud confirmada · Zenithradiar";
    }
  }, []);

  // Progress animation: step 0 -> 1 at ~2.5s, 1 -> 2 at ~6s
  useEffect(() => {
    const t1 = setTimeout(() => setProgress(1), 2600);
    const t2 = setTimeout(() => setProgress(2), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Live feed rotation
  useEffect(() => {
    let idx = 4;
    const interval = setInterval(() => {
      const next = LIVE_FEED_SEED[idx % LIVE_FEED_SEED.length];
      idx += 1;
      setFeed((cur) => {
        const updated = [{ ...next, minutesAgo: Math.max(1, Math.floor(Math.random() * 20)) }, ...cur].slice(0, 5);
        return updated;
      });
    }, 6500);
    return () => clearInterval(interval);
  }, []);

  // Chat auto-open with an initial message
  useEffect(() => {
    const t1 = setTimeout(() => setTyping(true), 3500);
    const t2 = setTimeout(() => {
      setTyping(false);
      setChatMessages([
        {
          author: "Zenithradiar",
          text: "¡Hola! Somos el equipo de soporte de Zenithradiar en Buenos Aires. Recibimos tu solicitud y un asesor está revisando tu caso. ¿Necesitás aclarar algo mientras tanto?",
          time: "recién",
        },
      ]);
      setChatBadge(1);
    }, 5200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const progressPct = progress === 0 ? 12 : progress === 1 ? 55 : 100;

  const statusText = progress === 0
    ? "Procesando tu solicitud"
    : progress === 1
      ? "Asignando un asesor de tu región"
      : "Preparando la conexión";

  const openChat = () => {
    setChatOpen(true);
    setChatBadge(0);
  };

  return (
    <div className="ty-root">
      {/* Top nav */}
      <header className="ty-topbar">
        <div className="ty-topbar__inner">
          <Logo />
          <Link to="/ar" className="ty-back" data-testid="thankyou-back">Volver al inicio</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="ty-hero">
        <div className="ty-hero__glow" aria-hidden />
        <div className="ty-hero__content">
          <div className="ty-status" data-testid="thankyou-status">
            <span className="ty-status__dot" />
            <span>Solicitud activa</span>
          </div>
          <h1 className="ty-title" data-testid="thankyou-title">Tu registro ha sido confirmado</h1>
          <p className="ty-subtitle">Un asesor certificado está siendo asignado a tu cuenta analítica</p>
          <p className="ty-timestamp">{submittedAt}</p>
          <div className="ty-refline">
            <span className="ty-refline__label">N.º de referencia</span>
            <span className="ty-refline__value" data-testid="thankyou-reference">{reference}</span>
          </div>
        </div>
      </section>

      {/* PROGRESS */}
      <div className="ty-progress" data-testid="thankyou-progress">
        <div className="ty-progress__track">
          <div className="ty-progress__bg">
            <div className="ty-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>

          <ProgressDot done={progress >= 0} label={<>Solicitud<br/>recibida</>} />
          <ProgressDot done={progress >= 1} label={<>Asesor<br/>asignado</>} />
          <ProgressDot done={progress >= 2} label={<>Conexión</>} />
        </div>
        <div className="ty-progress__status">
          <strong>{statusText}</strong>
          <span className="ty-dots"><i /><i /><i /></span>
        </div>
      </div>

      {/* MAIN */}
      <main className="ty-main">
        {/* CALL ALERT */}
        <section className="ty-card ty-call">
          <div className="ty-call__head">
            <div className="ty-call__icon"><PhoneCall size={18} /></div>
            <h3>Recibirás una llamada en breve</h3>
          </div>
          <p>
            Un asesor certificado de Zenithradiar se comunicará con vos para guiarte paso a paso en la configuración de tu espacio personal y responder tus consultas.
          </p>
          <div className="ty-notice">
            <strong>Acerca de tu asesor</strong>
            <p>
              Debido a la alta demanda de nuevas altas en Argentina, tu solicitud puede ser atendida por uno de nuestros asesores acreditados desde Uruguay, Chile, España, Colombia o México. Todos cuentan con certificación en tecnologías de análisis de datos y están capacitados para atenderte en español rioplatense o estándar.
            </p>
          </div>
        </section>

        {/* LIVE FEED */}
        <section className="ty-card ty-feed">
          <div className="ty-feed__head">
            <span className="ty-feed__dot" />
            <span className="ty-feed__title">Registros en tiempo real</span>
          </div>
          <ul className="ty-feed__list" data-testid="thankyou-feed">
            {feed.map((f, i) => (
              <li key={`${f.name}-${i}`} className="ty-feed__item">
                <div className="ty-feed__avatar">{f.initials}</div>
                <div className="ty-feed__text">
                  <strong>{f.name}</strong> de {f.city} acaba de registrarse
                </div>
                <div className="ty-feed__time"><Clock size={11} /> hace {f.minutesAgo} min</div>
              </li>
            ))}
          </ul>
        </section>

        {/* STEPS */}
        <section className="ty-card ty-steps">
          <h2>Lo que sucederá a continuación</h2>

          <div className="ty-step">
            <div className="ty-step__circle"><PhoneCall size={16} /></div>
            <div className="ty-step__body">
              <h4>Llamada de bienvenida</h4>
              <p>Tu asesor personal repasará tu perfil, experiencia previa y objetivos como usuario en Argentina.</p>
            </div>
          </div>

          <div className="ty-step">
            <div className="ty-step__circle"><Settings size={16} /></div>
            <div className="ty-step__body">
              <h4>Configuración de tu cuenta</h4>
              <p>Juntos ajustarán la plataforma según tus intereses de análisis y tu nivel de familiaridad con paneles de datos.</p>
            </div>
          </div>

          <div className="ty-step">
            <div className="ty-step__circle"><Layers size={16} /></div>
            <div className="ty-step__body">
              <h4>Activación de tu panel</h4>
              <p>Tu espacio de análisis quedará activado y podrás comenzar a leer el panorama en tu idioma y moneda local.</p>
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="ty-card ty-trust">
          <div className="ty-trust__head">
            <div className="ty-trust__badge" aria-hidden>
              <svg viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="118" height="38" rx="8" fill="#F7EEDD" stroke="#E6D6B0" />
                <text x="60" y="24" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="14" fill="#C43B4B">Banco Galicia</text>
              </svg>
            </div>
            <div className="ty-trust__headText">
              <strong>Verificación bancaria argentina</strong>
              <span>Procesamiento de identidad y comprobantes en Argentina</span>
            </div>
          </div>
          <div className="ty-trust__body">
            <p>
              Cuando corresponde adjuntar comprobantes de identidad o domicilio, se procesan a través de la infraestructura bancaria argentina, garantizando seguridad y confiabilidad en cada trámite. Nuestro dato de referencia institucional está registrado en Banco Galicia (CUIT 30-71892456-3).
            </p>
            <div className="ty-trust__grid">
              <div className="ty-trust__item">
                <div className="ty-trust__val">14K+</div>
                <div className="ty-trust__lbl">Cuentas activas</div>
              </div>
              <div className="ty-trust__item">
                <div className="ty-trust__val">23</div>
                <div className="ty-trust__lbl">Provincias cubiertas</div>
              </div>
              <div className="ty-trust__item">
                <div className="ty-trust__val">2022</div>
                <div className="ty-trust__lbl">Desde</div>
              </div>
            </div>
          </div>

          <div className="ty-trust__phones" aria-hidden>
            <svg viewBox="0 0 480 240" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="phBg" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#F5EDE0" />
                  <stop offset="100%" stopColor="#E7DCC8" />
                </linearGradient>
              </defs>
              <rect x="0" y="0" width="480" height="240" rx="14" fill="url(#phBg)" />
              {/* Left phone */}
              <g transform="translate(60 20)">
                <rect x="0" y="0" width="140" height="200" rx="18" fill="#0B0F14" />
                <rect x="6" y="10" width="128" height="180" rx="12" fill="#151b26" />
                <text x="70" y="36" textAnchor="middle" fontFamily="Inter" fontSize="10" fill="#C9F14B">ZR PANEL</text>
                <rect x="16" y="48" width="108" height="38" rx="6" fill="#1e2532" />
                <rect x="22" y="58" width="60" height="6" rx="2" fill="#C9F14B" />
                <rect x="22" y="70" width="90" height="4" rx="2" fill="#5b6270" />
                <rect x="16" y="94" width="108" height="14" rx="3" fill="#232a38" />
                <rect x="16" y="114" width="108" height="14" rx="3" fill="#232a38" />
                <rect x="16" y="134" width="108" height="14" rx="3" fill="#232a38" />
                <rect x="16" y="154" width="70" height="14" rx="3" fill="#C9F14B" opacity="0.85" />
              </g>
              {/* Right phone */}
              <g transform="translate(280 20)">
                <rect x="0" y="0" width="140" height="200" rx="18" fill="#F7EEDD" stroke="#E6D6B0" />
                <rect x="6" y="10" width="128" height="180" rx="12" fill="#ffffff" />
                <text x="70" y="38" textAnchor="middle" fontFamily="Inter" fontWeight="700" fontSize="12" fill="#C43B4B">Banco Galicia</text>
                <rect x="16" y="52" width="108" height="32" rx="6" fill="#F5EDE0" />
                <text x="22" y="66" fontFamily="Inter" fontSize="8" fill="#7a6f56">Titular</text>
                <text x="22" y="78" fontFamily="Inter" fontWeight="600" fontSize="9" fill="#242424">Zenithradiar S.A.S.</text>
                <rect x="16" y="94" width="108" height="20" rx="5" fill="#F5EDE0" />
                <text x="22" y="107" fontFamily="Inter" fontSize="8" fill="#242424">CBU 0070 1560 20…</text>
                <rect x="16" y="120" width="108" height="20" rx="5" fill="#F5EDE0" />
                <text x="22" y="133" fontFamily="Inter" fontSize="8" fill="#242424">Alias zenith.radiar…</text>
                <rect x="16" y="148" width="108" height="20" rx="5" fill="#C43B4B" />
                <text x="70" y="161" textAnchor="middle" fontFamily="Inter" fontWeight="600" fontSize="9" fill="#ffffff">Verificar</text>
              </g>
            </svg>
          </div>

          <div className="ty-trust__footer">
            <ShieldCheck size={14} /> Datos protegidos con encriptación de nivel bancario · Cumplimiento con la Ley 25.326 y directivas de la AAIP.
          </div>
        </section>

        {/* Contact + disclaimer */}
        <section className="ty-card ty-contact">
          <h3>¿Necesitás asistencia inmediata?</h3>
          <p>Escribinos o llamanos en horario de oficina de Buenos Aires (lunes a viernes, 9 a 18 h).</p>
          <div className="ty-contact__actions">
            <a href="mailto:contacto@zenithradiar.com" className="ty-btn ty-btn--primary" data-testid="thankyou-email">
              contacto@zenithradiar.com
            </a>
            <a href="tel:+541153527894" className="ty-btn ty-btn--ghost" data-testid="thankyou-phone">
              +54 11 5352-7894
            </a>
          </div>
        </section>

        <div className="ty-disclaimer">
          El material entregado por Zenithradiar tiene un propósito puramente orientativo y general. No representa una consultoría financiera, profesional ni a medida, ni asegura resultados específicos. Zenithradiar S.A.S. · CUIT 30-71892456-3 · registro conforme a la Ley 25.326 de Protección de Datos Personales (República Argentina).
        </div>
      </main>

      {/* CHAT WIDGET */}
      <button
        type="button"
        className="ty-chat-bubble"
        onClick={openChat}
        aria-label="Abrir chat de soporte"
        data-testid="thankyou-chat-open"
      >
        <MessageCircle size={20} />
        {chatBadge > 0 && !chatOpen && <span className="ty-chat-badge">{chatBadge}</span>}
      </button>

      {chatOpen && (
        <aside className="ty-chat-window" role="dialog" aria-label="Chat de soporte">
          <header className="ty-chat__header">
            <div className="ty-chat__avatar"><User size={16} /></div>
            <div className="ty-chat__headText">
              <strong>Soporte Zenithradiar</strong>
              <span><i className="ty-chat__online" /> En línea</span>
            </div>
            <button
              type="button"
              className="ty-chat__close"
              onClick={() => setChatOpen(false)}
              aria-label="Cerrar chat"
              data-testid="thankyou-chat-close"
            >
              <X size={16} />
            </button>
          </header>
          <div className="ty-chat__body">
            {chatMessages.map((m, i) => (
              <div key={i} className="ty-chat__msg">
                <div className="ty-chat__msgAuthor">{m.author} · <span>{m.time}</span></div>
                <div className="ty-chat__msgText">{m.text}</div>
              </div>
            ))}
            {typing && (
              <div className="ty-chat__typing"><i /><i /><i /></div>
            )}
          </div>
          <footer className="ty-chat__footer">
            <a href="mailto:contacto@zenithradiar.com" className="ty-chat__mail">
              Continuar por correo
            </a>
          </footer>
        </aside>
      )}

      <footer className="ty-footer">
        Zenithradiar S.A.S. · CUIT 30-71892456-3 · Av. del Libertador 4801, Piso 7 · C1426 CABA · Buenos Aires · Argentina
        <span>© 2026 Zenithradiar S.A.S. Todos los derechos reservados.</span>
      </footer>
    </div>
  );
}

function ProgressDot({ done, label }) {
  return (
    <div className={`ty-pdot ${done ? "is-done" : ""}`}>
      <div className="ty-pdot__circle">
        {done ? <CheckCircle2 size={14} /> : <span className="ty-pdot__inner" />}
      </div>
      <span className="ty-pdot__label">{label}</span>
    </div>
  );
}
